import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { getBatchStatus, getBatchHistory, getAuditLog } from "./api";

const UploadContext = createContext(null);

const STORAGE_KEY = "ionm_processing_files";

function loadProcessingFiles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveProcessingFiles(filenames) {
  try {
    if (filenames.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filenames));
    }
  } catch { /* silent */ }
}

export function UploadProvider({ children }) {
  // batches: [{ batch_id, name, total, completed, passed, failed, status, files, startTime }]
  const [batches, setBatches]           = useState([]);
  // singles: [{ filename, status: PROCESSING|COMPLETE|ERROR, pct }]
  const [singles, setSingles]           = useState([]);
  const batchPollRefs                   = useRef({});
  const singlePollRefs                  = useRef({});
  const resumedRef                      = useRef(false);

  // ── Resume polling for any files that were processing before refresh ────────
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;

    // Resume singles from localStorage
    const saved = loadProcessingFiles();
    if (saved.length > 0) {
      setSingles(saved.map(fn => ({ filename: fn, status: "PROCESSING", pct: 0 })));
      saved.forEach(fn => startPolling(fn));
    }

    // Resume active batches from API
    getBatchHistory().then(batches => {
      const threeHoursAgo = Date.now() - (3 * 60 * 60 * 1000);
      const active = batches.filter(b =>
        b.status === "PROCESSING" &&
        new Date(b.created_at).getTime() > threeHoursAgo
      );
      if (!active.length) return;

      active.forEach(b => {
        getBatchStatus(b.id).then(data => {
          const entry = {
            batch_id:  b.id,
            name:      b.name,
            total:     data.total,
            completed: data.completed,
            passed:    data.passed,
            failed:    data.failed,
            status:    data.status,
            files:     data.files || [],
            startTime: new Date(b.created_at).getTime(),
          };
          setBatches(prev => {
            if (prev.find(x => x.batch_id === b.id)) return prev;
            return [entry, ...prev];
          });

          if (data.status !== "COMPLETE") {
            batchPollRefs.current[b.id] = setInterval(async () => {
              try {
                const latest = await getBatchStatus(b.id);
                setBatches(prev => prev.map(x =>
                  x.batch_id === b.id ? { ...x, ...latest, startTime: x.startTime } : x
                ));
                if (latest.status === "COMPLETE") {
                  clearInterval(batchPollRefs.current[b.id]);
                  delete batchPollRefs.current[b.id];
                }
              } catch { /* silent */ }
            }, 5000);
          }
        }).catch(() => {});
      });
    }).catch(() => {});
  }, []);

  // ── Keep localStorage in sync with active singles ──────────────────────────
  useEffect(() => {
    const active = singles.filter(s => s.status === "PROCESSING").map(s => s.filename);
    saveProcessingFiles(active);
  }, [singles]);

  // ── Shared polling logic ───────────────────────────────────────────────────
  const startPolling = useCallback((filename) => {
    // Cancel any existing poll for this filename
    if (singlePollRefs.current[filename]) {
      clearInterval(singlePollRefs.current[filename]);
      delete singlePollRefs.current[filename];
    }

    const STEPS = [
      "Document Downloaded", "Content Extracted", "Content Indexed",
      "QA Checks Completed", "QA Results Indexed", "Processing Complete"
    ];
    let attempts = 0;
    const MAX    = 90;

    const poll = async () => {
      try {
        const events = await getAuditLog(filename);
        const names  = events.map(e => e.event);
        const done    = names.includes("Processing Complete");
        const errored = names.includes("Processing Error") || events.some(e => e.status === "error") || ++attempts >= MAX;
        const pct    = done ? 100 : Math.round((STEPS.filter(s => names.includes(s)).length / STEPS.length) * 100);

        setSingles(prev => prev.map(s =>
          s.filename === filename
            ? { ...s, pct: Math.max(s.pct, pct), status: done ? "COMPLETE" : errored ? "ERROR" : "PROCESSING" }
            : s
        ));

        if (done || errored) {
          clearInterval(singlePollRefs.current[filename]);
          delete singlePollRefs.current[filename];
        }
      } catch {
        if (++attempts >= MAX) {
          setSingles(prev => prev.map(s => s.filename === filename ? { ...s, status: "ERROR" } : s));
          clearInterval(singlePollRefs.current[filename]);
          delete singlePollRefs.current[filename];
        }
      }
    };

    // For resumed files, poll immediately (they may already be done)
    // For new uploads, delay 15s before first poll
    poll();
    singlePollRefs.current[filename] = setInterval(poll, 15000);
  }, []);

  // ── Batch tracking ──────────────────────────────────────────────────────────
  const trackBatch = useCallback((batchResult, files) => {
    const entry = {
      batch_id:  batchResult.batch_id,
      name:      batchResult.name || `Batch ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      total:     batchResult.total,
      completed: 0,
      passed:    0,
      failed:    0,
      status:    "PROCESSING",
      files:     files.map(f => ({ filename: f.name, status: "PROCESSING" })),
      startTime: Date.now(),
    };
    setBatches(prev => [entry, ...prev]);

    // Poll every 5s until complete
    batchPollRefs.current[entry.batch_id] = setInterval(async () => {
      try {
        const data = await getBatchStatus(entry.batch_id);
        setBatches(prev => prev.map(b =>
          b.batch_id === entry.batch_id
            ? { ...b, ...data, startTime: b.startTime }
            : b
        ));
        if (data.status === "COMPLETE") {
          clearInterval(batchPollRefs.current[entry.batch_id]);
          delete batchPollRefs.current[entry.batch_id];
        }
      } catch { /* silent */ }
    }, 5000);
  }, []);

  // ── Single file tracking ────────────────────────────────────────────────────
  const trackSingle = useCallback((filename) => {
    // Always reset — re-uploading same filename should start fresh
    setSingles(prev => {
      const existing = prev.find(s => s.filename === filename);
      if (existing) {
        return prev.map(s => s.filename === filename
          ? { filename, status: "PROCESSING", pct: 0 }
          : s
        );
      }
      return [{ filename, status: "PROCESSING", pct: 0 }, ...prev];
    });

    // Start polling with initial 15s delay for new uploads
    if (singlePollRefs.current[filename]) {
      clearInterval(singlePollRefs.current[filename]);
      delete singlePollRefs.current[filename];
    }

    const STEPS = [
      "Document Downloaded", "Content Extracted", "Content Indexed",
      "QA Checks Completed", "QA Results Indexed", "Processing Complete"
    ];
    let attempts = 0;
    const MAX    = 90;

    const poll = async () => {
      try {
        const events = await getAuditLog(filename);
        const names  = events.map(e => e.event);
        const done    = names.includes("Processing Complete");
        const errored = names.includes("Processing Error") || events.some(e => e.status === "error") || ++attempts >= MAX;
        const pct    = done ? 100 : Math.round((STEPS.filter(s => names.includes(s)).length / STEPS.length) * 100);

        setSingles(prev => prev.map(s =>
          s.filename === filename
            ? { ...s, pct: Math.max(s.pct, pct), status: done ? "COMPLETE" : errored ? "ERROR" : "PROCESSING" }
            : s
        ));

        if (done || errored) {
          clearInterval(singlePollRefs.current[filename]);
          delete singlePollRefs.current[filename];
        }
      } catch {
        if (++attempts >= MAX) {
          setSingles(prev => prev.map(s => s.filename === filename ? { ...s, status: "ERROR" } : s));
          clearInterval(singlePollRefs.current[filename]);
          delete singlePollRefs.current[filename];
        }
      }
    };

    setTimeout(() => {
      poll();
      singlePollRefs.current[filename] = setInterval(poll, 15000);
    }, 15000);
  }, []);

  const clearCompleted = useCallback(() => {
    setBatches(prev => prev.filter(b => b.status !== "COMPLETE"));
    setSingles(prev => prev.filter(s => s.status === "PROCESSING"));
  }, []);

  // ── Derived counts ──────────────────────────────────────────────────────────
  const activeBatches  = batches.filter(b => b.status !== "COMPLETE");
  const activeSingles  = singles.filter(s => s.status === "PROCESSING");
  const activeCount    = activeBatches.length + activeSingles.length;
  const totalDocs      = activeBatches.reduce((s, b) => s + b.total, 0) + activeSingles.length;
  const completedDocs  = activeBatches.reduce((s, b) => s + b.completed, 0)
                       + activeSingles.filter(s => s.status === "COMPLETE").length;

  return (
    <UploadContext.Provider value={{
      batches, singles,
      activeBatches, activeSingles,
      activeCount, totalDocs, completedDocs,
      trackBatch, trackSingle, clearCompleted,
    }}>
      {children}
    </UploadContext.Provider>
  );
}

export const useUpload = () => useContext(UploadContext);